import { z } from 'zod';
import {
  bigintString,
  captureHealth,
  captureReason,
  closingMeeting,
  commitmentStatus,
  direction,
  evidence,
  isoDate,
  linkAccess,
  nameSource,
  noteRole,
  span,
  spaceKind,
  translationState,
  uuid,
} from './common';
import { dispatchRefusal } from './calendar';

/** A row in the meetings list. Describes what the meeting produced, not just when it was. */
export const meetingSummary = z.object({
  id: uuid,
  title: z.string(),
  startedAt: isoDate,
  endedAt: isoDate.nullable(),
  spaceId: uuid,
  /** NULL for a bot recording: it belongs to the meeting and the space, not to a person. */
  recordedById: uuid.nullable(),
  /** IMPORT is a meeting brought in from another product (PRD 37): their transcript, their note. */
  source: z.enum(['BOT', 'MAC', 'IMPORT']),
  captureHealth,
  /** Why nothing was recorded, for a meeting that FAILED; null otherwise (2026-10-08). */
  captureReason: captureReason.nullable(),
  language: z.string(),
  attendees: z.array(z.string()),
  /** The series it rolls forward in, when it is in one — a row can say so. */
  seriesKey: z.string().nullable(),
  enhanced: z.boolean(),
  /** The note's first sentence — what a row says about the hour. Null until written. */
  lead: z.string().nullable(),
  /** What it produced — the reason to open it. */
  commitmentCount: z.number().int(),
  decisionCount: z.number().int(),
  riskCount: z.number().int(),
  /** Open ones only: an answered question is not something the list is for. */
  openQuestionCount: z.number().int(),
});
export type MeetingSummary = z.infer<typeof meetingSummary>;

export const transcriptLine = z.object({
  id: uuid,
  seq: bigintString,
  text: z.string(),
  lang: z.string(),
  speakerName: z.string().nullable(),
  /** Recall gives the participant's email. It is the join key for people and for Slack. */
  speakerEmail: z.string().nullable(),
  nameSource: nameSource.nullable(),
  spokeAt: isoDate.nullable(),
  spokeUntil: isoDate.nullable(),
  /** A person fixed the words or the speaker; the original is kept on the row. */
  corrected: z.boolean(),
});
export type TranscriptLine = z.infer<typeof transcriptLine>;

/**
 * One message typed in the call (PRD 50).
 *
 * The only part of a meeting that is already written down: a link, a spelling
 * or a figure from the chat is exact where the transcript guesses. It is
 * attached to the transcript, never merged into it — it was typed, not said —
 * and only messages addressed to EVERYONE ever reach here.
 */
export const chatMessage = z.object({
  id: uuid,
  authorName: z.string().nullable(),
  authorEmail: z.string().nullable(),
  text: z.string(),
  /** Shown as themselves. Never fetched, never unfurled, never followed. */
  links: z.array(z.string()),
  sentAt: isoDate,
  /** The notetaker's own — its notice, a pause, an answer to "Ptitim, …". Optional for an older api. */
  byNotetaker: z.boolean().optional(),
});
export type ChatMessage = z.infer<typeof chatMessage>;

/**
 * Somebody arriving in the call or leaving it (2026-09-25), shown in the
 * transcript as a line of its own. The transcript's reader, and its retention.
 */
export const presenceEvent = z.object({
  id: uuid,
  name: z.string().nullable(),
  email: z.string().nullable(),
  kind: z.enum(['joined', 'left']),
  at: isoDate,
});
export type PresenceEvent = z.infer<typeof presenceEvent>;

/** Fixing one line: the words, the speaker, or both. Empty = nothing to change. */
export const correctLineBody = z.object({
  text: z.string().trim().min(1).max(4000).optional(),
  speakerName: z.string().trim().min(1).max(200).nullable().optional(),
  speakerEmail: z.string().trim().email().max(320).nullable().optional(),
});
export type CorrectLineBody = z.infer<typeof correctLineBody>;

export const commitment = z.object({
  id: uuid,
  text: z.string(),
  ownerName: z.string().nullable(),
  ownerEmail: z.string().nullable(),
  grade: evidence,
  status: commitmentStatus,
  meetingId: uuid,
  spaceId: uuid,
  span,
  /**
   * Derived per reader from ownerEmail, never stored: the same row is "I owe"
   * to one person and "owed to me" to another.
   */
  direction,
  postedToSlack: z.boolean(),
  /**
   * When it is due, if anybody said. Optional and never guessed — most of what
   * is promised in a meeting has no date, and a product that invents them is a
   * product whose dates nobody believes.
   *
   * ⚠ It is HERE as well as on the ledger's own shape because a commitment is
   * read on the meeting it was made in at least as often as on the Open page,
   * and until 2026-09-23 this field simply was not sent: the deadline control
   * could not be offered on the meeting because the meeting did not know.
   */
  dueAt: isoDate.nullable(),
  /**
   * ⚠ Here for the same reason as `dueAt`, and missing for the same reason:
   * every control a commitment has — reword, deadline, send to Asana — needs
   * the row to carry what it acts on, and the meeting's copy carried less than
   * the ledger's. Vladimir, 2026-09-23: *"add reword and send to asana buttons
   * to all commitments, now I can't do this in the meeting (same problem that
   * was with deadline)"*. Twice is a pattern: this shape and the ledger's
   * should be compared whenever either grows.
   */
  seriesKey: z.string().nullable(),
  /** Where it went, when somebody sent it on. Null until they did. */
  asanaTaskUrl: z.string().nullable(),
  createdAt: isoDate,
  closedAt: isoDate.nullable(),
  /** When the product closed it by listening: the later meeting that said it was done. */
  closedByMeeting: closingMeeting.nullable(),
  /** A hand-over's successor says where it came from. */
  handedOverFrom: z.object({ name: z.string().nullable(), email: z.string() }).nullable(),
});
export type Commitment = z.infer<typeof commitment>;

export const decision = z.object({
  id: uuid,
  text: z.string(),
  grade: evidence,
  status: z.enum(['STANDS', 'SUPERSEDED', 'RETIRED']),
  meetingId: uuid,
  spaceId: uuid,
  span,
  supersedesId: uuid.nullable(),
  createdAt: isoDate,
});
export type Decision = z.infer<typeof decision>;

/**
 * A risk somebody raised (PRD 54).
 *
 * ⚠ Not a problem and not a commitment: a stated worry about something that
 * has **not** happened. Its whole value is in being written down before it
 * costs anything, which is why HAPPENED is a status and not a deletion.
 */
export const risk = z.object({
  id: uuid,
  text: z.string(),
  /** What it threatens, in the meeting's own words. */
  detail: z.string().nullable(),
  grade: evidence,
  status: z.enum(['OPEN', 'ACCEPTED', 'AVOIDED', 'HAPPENED']),
  meetingId: uuid,
  spaceId: uuid,
  span,
  createdAt: isoDate,
});
export type Risk = z.infer<typeof risk>;

/**
 * A question the meeting raised and did not settle (PRD 54).
 *
 * ⚠ Only ever an **unanswered** one. A question answered in the next sentence
 * is a decision, and a transcript full of question marks would otherwise make
 * this the noisiest list in the product.
 */
export const question = z.object({
  id: uuid,
  text: z.string(),
  detail: z.string().nullable(),
  grade: evidence,
  status: z.enum(['OPEN', 'ANSWERED', 'DROPPED']),
  meetingId: uuid,
  spaceId: uuid,
  span,
  /** WHERE it was answered — never a copy of the answer. */
  answeredIn: z.object({ id: uuid, title: z.string(), startedAt: isoDate }).nullable(),
  answeredAt: isoDate.nullable(),
  createdAt: isoDate,
});
export type Question = z.infer<typeof question>;

/** One meeting, in full. Projected field by field — never spread from a row. */
export const meetingDetail = meetingSummary.extend({
  notes: z.string(),
  polishedNotes: z.string().nullable(),
  enhancedAt: isoDate.nullable(),
  attendeeEmails: z.array(z.string()),
  seriesKey: z.string().nullable(),
  /**
   * The template its series writes every meeting up with (PRD 65), and this
   * meeting's write-up with it once there is one: the page opens on that, the
   * full note one click away. Null when the series has none, or for a reader
   * who may not read write-ups.
   */
  seriesNote: z
    .object({ templateId: z.string(), templateName: z.string(), writeupId: uuid.nullable() })
    .nullable(),
  titleIsAuto: z.boolean(),
  /** The folders this reader can see that hold it — by a rule, or pinned by hand. */
  folders: z.array(
    z.object({
      id: uuid,
      name: z.string(),
      spaceId: uuid.nullable(),
      /** True when a person put it here; false when the folder's rule caught it. */
      pinned: z.boolean(),
    }),
  ),
  linkAccess,
  /**
   * The channels it is posted in that this reader is in (PRD 58), and whether
   * they may take it out of each — they posted it, own the channel, or manage
   * the meeting.
   */
  channels: z.array(z.object({ id: uuid, name: z.string(), canRemove: z.boolean() })),
  /** What THIS reader may do — so the client shows exactly the controls that will work. */
  access: z.object({
    canEdit: z.boolean(),
    canManage: z.boolean(),
    canReadTranscript: z.boolean(),
  }),
  /**
   * What the notetaker is doing, and what this reader may do about it.
   *
   * Since PRD 43 this is a live thing rather than a report: the meeting's page
   * exists from the moment the bot is sent, so it carries the controls for
   * every state the bot can be in — and `eventId`, so the notetaker can be
   * sent again from the page of the meeting it left. Null when no bot was
   * involved (an import, a meeting somebody wrote by hand).
   */
  capture: z
    .object({
      health: captureHealth,
      scheduledFor: isoDate,
      joinedAt: isoDate.nullable(),
      endedAt: isoDate.nullable(),
      recordedSeconds: z.number().int().nullable(),
      runId: uuid,
      /** The person who pressed Stop in Ptitim, when somebody did. */
      stoppedBy: z.string().nullable(),
      canPause: z.boolean(),
      canResume: z.boolean(),
      canStop: z.boolean(),
    })
    .nullable(),
  /**
   * The calendar entry this meeting **is** (PRD 44).
   *
   * Present from the moment the call is on a calendar, with or without a
   * notetaker — which is why it is here and not inside `capture`: a meeting
   * booked for Thursday has an entry and no recording at all.
   */
  event: z
    .object({
      id: uuid,
      /**
       * The dispatcher's own verdict, while the meeting is still ahead — the
       * SAME `decideDispatch` the week-ahead row shows, so the page and the
       * row cannot say different things. Null once something has recorded,
       * when it is no longer a question.
       *
       * ⚠ Without this the page had only `captureHealth`, which is SCHEDULED
       * for **every** meeting born from the calendar, booked or not — so it
       * told Vladimir "Booked for this meeting. It joins as the meeting
       * starts" about a meeting he had just turned off (2026-09-22).
       */
      plan: z.union([z.literal('WILL_RECORD'), dispatchRefusal]).nullable(),
      /** A person's answer for this one meeting: true records, false skips, null follows the series. */
      recordOverride: z.boolean().nullable(),
      /** A notetaker is actually booked at Recall — not merely expected. */
      booked: z.boolean(),
    })
    .nullable(),
  /** A notetaker can be sent: there is a link, and none is in the call. */
  canSendBot: z.boolean(),
  /**
   * What is still being made from this meeting (PRD 47).
   *
   * ⚠ True means "on its way", not "missing". An empty tab is the same
   * picture whether the thing is coming in forty seconds or failed an hour
   * ago, and Vladimir put it exactly: *"I don't understand if it's just
   * processing or something is already broke"*. Every one of these is a fact
   * the server knows — a timestamp that is null — and none of them is a guess
   * from an empty list.
   */
  processing: z.object({
    /** The written note. */
    note: z.boolean(),
    /** Commitments and decisions. */
    ledger: z.boolean(),
    /** The walkthrough. */
    walkthrough: z.boolean(),
    /**
     * ⚠ And the other half of the same promise: something above has been "on
     * its way" for long enough that it is not on its way. A band that says
     * "writing up…" for ever is the bug this whole field exists to close, only
     * with nicer wording.
     */
    stalled: z.boolean(),
  }),
  /** Whether `GET /v1/meetings/:id/video` will answer for this reader, and how long we keep it. */
  video: z.object({
    available: z.boolean(),
    keptUntil: isoDate.nullable(),
    /**
     * The wall-clock moment the video's 00:00 is, so the player, the
     * transcript and the walkthrough's sections share one clock (PRD 55).
     * Null on a meeting recorded before this was written down, and the player
     * then simply does not follow along.
     */
    startedAt: isoDate.nullable(),
  }),
  /**
   * The frames people asked for out loud — "Ptitim, screenshot" (PRD 64) — in
   * the order they were asked. Under the video's rule: none for a reader who
   * may not read the transcript. `url` is a signed link for fifteen minutes,
   * present only once the frame is cut. Optional only for an older api.
   */
  screenshots: z
    .array(
      z.object({
        id: uuid,
        at: isoDate,
        offsetSeconds: z.number(),
        status: z.enum(['PENDING', 'READY', 'FAILED']),
        url: z.string().nullable(),
        askedBy: z.string().nullable(),
      }),
    )
    .optional(),
  /**
   * The transcript, for this reader: when the workspace's retention removed it
   * (so the panel can say why it is empty), and when it will — null for "kept".
   * Both null for a reader who may not read the transcript at all.
   */
  transcript: z.object({ removedAt: isoDate.nullable(), keptUntil: isoDate.nullable() }),
  commitments: z.array(commitment),
  decisions: z.array(decision),
  risks: z.array(risk),
  questions: z.array(question),
  /** Commitments from earlier meetings that THIS conversation closed — "closed here, by what was said". */
  closedHere: z.array(commitment),
  /** Shown in the reader's language where that is not the one it was written in (PRD 57). */
  translation: translationState.nullable(),
  /**
   * The note ITSELF is written in another language than this reader's: shown
   * translated, or about to be. `translation` says the same of the page as a
   * whole — one foreign action item is enough for it — and a note is edited
   * in place only when what is on screen is the note as it was written.
   */
  noteForeign: z.boolean().default(false),
});
export type MeetingDetail = z.infer<typeof meetingDetail>;

/**
 * One stretch of a meeting, retold (PRD 47).
 *
 * `at` is milliseconds from the start, which the client renders as the
 * `00:14:02` a reader scans for. `startSegmentId` is what makes a section
 * openable in the transcript at the words it is retelling.
 */
export const meetingChapter = z.object({
  id: uuid,
  seq: z.number().int(),
  title: z.string(),
  body: z.string(),
  at: z.number().int(),
  until: z.number().int().nullable(),
  startSegmentId: uuid.nullable(),
});
export type MeetingChapter = z.infer<typeof meetingChapter>;

/** Browse by container. A filter narrows what you can already see; it never widens it. */
export const meetingsQuery = z.object({
  space: uuid.optional(),
  folder: uuid.optional(),
  /** Meetings this person was in. */
  person: z.string().email().optional(),
  series: z.string().min(1).max(200).optional(),
  /** Those that started in this window — a week of the calendar (PRD 67). */
  from: isoDate.optional(),
  to: isoDate.optional(),
});
export type MeetingsQuery = z.infer<typeof meetingsQuery>;

/** Title and your typed notes are the two things a person writes on a meeting. */
export const updateMeetingBody = z.object({
  title: z.string().trim().min(1).max(200).optional(),
  notes: z.string().max(200_000).optional(),
  /** The written note, edited by a person. Null clears it back to "not written". */
  polishedNotes: z.string().max(200_000).nullable().optional(),
  /** Attach to a series (its key), or detach with null — so a one-off can roll forward with its room. */
  seriesKey: z.string().trim().min(1).max(200).nullable().optional(),
  /**
   * A name for the series being joined — only meaningful alongside
   * `seriesKey`, and only for one somebody is assembling by hand (PRD 45).
   *
   * A calendar's series is named by its meetings; one you put together
   * yourself has nothing to take a name from, and four meetings called "Test
   * event 1..4" would make a series called "Test event 4".
   */
  seriesTitle: z.string().trim().min(1).max(200).optional(),
});
export type UpdateMeetingBody = z.infer<typeof updateMeetingBody>;

/** Who has this meeting, and by which door — for the one person who manages it. */
export const meetingSharing = z.object({
  linkAccess,
  people: z.array(
    z.object({
      email: z.string(),
      name: z.string().nullable(),
      role: noteRole,
      createdAt: isoDate,
    }),
  ),
  spaces: z.array(z.object({ id: uuid, name: z.string(), kind: spaceKind })),
  folders: z.array(z.object({ id: uuid, name: z.string(), spaceId: uuid.nullable() })),
});
export type MeetingSharing = z.infer<typeof meetingSharing>;

export const linkAccessBody = z.object({ linkAccess });
export type LinkAccessBody = z.infer<typeof linkAccessBody>;

/**
 * The series a meeting plainly belongs to but was never filed into (PRD 53).
 *
 * ⚠ Its own read, not part of the meeting. It costs several queries for a
 * banner most meetings never show, and making the page wait for it delayed
 * the header controls enough to break a smoke story — which is the cheapest
 * possible warning that it did not belong there.
 */
export const seriesSuggestion = z
  .object({
    seriesKey: z.string(),
    title: z.string(),
    /** Named, so the banner says WHY rather than asking to be trusted. */
    because: z.object({ topics: z.array(z.string()), people: z.number().int() }),
  })
  .nullable();
export type SeriesSuggestionDto = z.infer<typeof seriesSuggestion>;

/** "No, this meeting is not one of those" (PRD 53). */
export const refuseSeriesBody = z.object({ seriesKey: z.string().min(1).max(200) });
export type RefuseSeriesBody = z.infer<typeof refuseSeriesBody>;

/** ✦ Rewrite: which parts of the write-up were asked for again. */
export const rewriteAnswer = z.object({
  asked: z.array(z.enum(['note', 'ledger', 'walkthrough'])),
});
export type RewriteAnswer = z.infer<typeof rewriteAnswer>;
